import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { resendVerification } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Slim banner shown at the top of the app when the current user's email
 * hasn't been verified yet. Google users and manager-invited users are
 * auto-verified so they never see this.
 */
export default function EmailVerificationBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified || dismissed) return null;

  const handleResend = async () => {
    setSending(true);
    try {
      await resendVerification();
      toast.success('Verification email sent — check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between gap-3 bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-900"
      data-testid="email-verification-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Mail className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">
          Please verify <strong>{user.email}</strong> to secure your account.
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={handleResend}
          disabled={sending}
          className="underline underline-offset-2 hover:text-amber-950 disabled:opacity-60"
          data-testid="banner-resend-verification"
        >
          {sending ? 'Sending…' : 'Resend email'}
        </button>
        <Link
          to="/verify-email"
          className="hidden sm:inline underline underline-offset-2 hover:text-amber-950"
        >
          Details
        </Link>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="p-1 rounded hover:bg-amber-100"
          data-testid="banner-dismiss-verification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
